import { BaseGridRenderer, RenderConfig } from './BaseGridRenderer';
import { HexGrid } from './HexGrid';
import { Cell, CellType, CellState } from './Cell';
import { HexMath, Point } from './HexMath';

export class HexGridRenderer extends BaseGridRenderer {
  protected grid: HexGrid;
  private offsetX: number = 0;
  private offsetY: number = 0;

  constructor(grid: HexGrid, config: RenderConfig) {
    super(grid, config);
    this.grid = grid;

    // resizeCanvas() was already called by super(), but field initializers ran after and reset offsetX/offsetY to 0
    // So we need to call it again to properly set the offsets
    this.resizeCanvas();
  }

  resizeCanvas(): void {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    for (let row = 0; row < this.grid.rows; row++) {
      for (let col = 0; col < this.grid.cols; col++) {
        const axial = HexMath.offsetToAxial(col, row, this.grid.orientation);
        const pixel = HexMath.hexToPixel(axial.q, axial.r, this.cellSize, this.grid.orientation);

        const corners = HexMath.getHexCorners(pixel.x, pixel.y, this.cellSize, this.grid.orientation);

        for (const corner of corners) {
          minX = Math.min(minX, corner.x);
          maxX = Math.max(maxX, corner.x);
          minY = Math.min(minY, corner.y);
          maxY = Math.max(maxY, corner.y);
        }
      }
    }

    const gridWidth = maxX - minX;
    const gridHeight = maxY - minY;

    const padding = this.cellSize / 2;
    const width = Math.ceil(gridWidth + padding * 2);
    const height = Math.ceil(gridHeight + padding * 2);

    this.canvas.width = width * this.dpr;
    this.canvas.height = height * this.dpr;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    this.offsetX = -minX + padding;
    this.offsetY = -minY + padding;
  }

  render(hoverCell?: Cell | null, previewType?: 'wall' | 'erase' | 'start' | 'goal' | null): void {
    this.clear();
    this.renderCells();
    if (hoverCell && previewType) {
      this.renderCellPreview(hoverCell, previewType);
      this.renderHoverHighlight(hoverCell);
    }
  }

  cellFromPoint(x: number, y: number): { row: number; col: number } | null {
    const adjustedX = x - this.offsetX;
    const adjustedY = y - this.offsetY;

    const hex = HexMath.pixelToHex(adjustedX, adjustedY, this.cellSize, this.grid.orientation);
    const offset = HexMath.axialToOffset(hex.q, hex.r, this.grid.orientation);

    if (this.grid.isInBounds(offset.row, offset.col)) {
      return { row: offset.row, col: offset.col };
    }

    return null;
  }

  private renderCells(): void {
    for (let row = 0; row < this.grid.rows; row++) {
      for (let col = 0; col < this.grid.cols; col++) {
        const cell = this.grid.getCellAt(row, col);
        this.renderCell(cell);
      }
    }
  }

  private renderCell(cell: Cell): void {
    const center = this.getCellCenter(cell);
    const corners = HexMath.getHexCorners(center.x, center.y, this.cellSize, this.grid.orientation);

    const color = this.getCellColor(cell);
    this.ctx.fillStyle = color;

    this.ctx.beginPath();
    this.ctx.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < corners.length; i++) {
      this.ctx.lineTo(corners[i].x, corners[i].y);
    }
    this.ctx.closePath();
    this.ctx.fill();

    if (this.showGrid) {
      this.ctx.strokeStyle = this.colors.grid;
      this.ctx.lineWidth = 1;
      this.ctx.stroke();
    }

    if (cell.type === CellType.START || cell.type === CellType.GOAL) {
      this.renderMarker(cell);
    }
  }

  private getCellColor(cell: Cell): string {
    switch (cell.type) {
      case CellType.WALL:
        return this.colors.wall;
      case CellType.START:
        return this.colors.start;
      case CellType.GOAL:
        return this.colors.goal;
      default:
        switch (cell.state) {
          case CellState.OPEN:
            return this.colors.open;
          case CellState.CLOSED:
            return this.colors.closed;
          case CellState.PATH:
            return this.colors.path;
          default:
            return this.colors.empty;
        }
    }
  }

  private renderMarker(cell: Cell): void {
    const center = this.getCellCenter(cell);
    const radius = this.cellSize / 2.5;

    this.ctx.fillStyle = '#ffffff';
    this.ctx.beginPath();
    this.ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
    this.ctx.fill();
  }

  private getCellCenter(cell: Cell): Point {
    const axial = HexMath.offsetToAxial(cell.col, cell.row, this.grid.orientation);
    const pixel = HexMath.hexToPixel(axial.q, axial.r, this.cellSize, this.grid.orientation);
    return {
      x: pixel.x + this.offsetX,
      y: pixel.y + this.offsetY
    };
  }

  private renderCellPreview(cell: Cell, previewType: 'wall' | 'erase' | 'start' | 'goal'): void {
    const center = this.getCellCenter(cell);
    const corners = HexMath.getHexCorners(center.x, center.y, this.cellSize, this.grid.orientation);

    this.ctx.globalAlpha = 0.5;

    let shouldRender = false;
    let fillColor = '';

    switch (previewType) {
      case 'wall':
        if (cell.type !== CellType.START && cell.type !== CellType.GOAL) {
          shouldRender = true;
          fillColor = this.colors.wall;
        }
        break;

      case 'erase':
        if (cell.type === CellType.WALL) {
          shouldRender = true;
          fillColor = this.colors.empty;
        }
        break;

      case 'start':
        shouldRender = true;
        fillColor = this.colors.start;
        break;

      case 'goal':
        shouldRender = true;
        fillColor = this.colors.goal;
        break;
    }

    if (shouldRender) {
      this.ctx.fillStyle = fillColor;
      this.ctx.beginPath();
      this.ctx.moveTo(corners[0].x, corners[0].y);
      for (let i = 1; i < corners.length; i++) {
        this.ctx.lineTo(corners[i].x, corners[i].y);
      }
      this.ctx.closePath();
      this.ctx.fill();

      if (previewType === 'start' || previewType === 'goal') {
        this.ctx.globalAlpha = 1.0;
        this.ctx.fillStyle = '#ffffff';
        this.ctx.beginPath();
        this.ctx.arc(center.x, center.y, this.cellSize / 2.5, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.globalAlpha = 0.5;
      }
    }

    this.ctx.globalAlpha = 1.0;
  }

  private renderHoverHighlight(cell: Cell): void {
    const center = this.getCellCenter(cell);
    const corners = HexMath.getHexCorners(center.x, center.y, this.cellSize, this.grid.orientation);

    this.ctx.strokeStyle = '#ffd700';
    this.ctx.lineWidth = 2;
    this.ctx.beginPath();
    this.ctx.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < corners.length; i++) {
      this.ctx.lineTo(corners[i].x, corners[i].y);
    }
    this.ctx.closePath();
    this.ctx.stroke();
  }

  protected getCellCenterX(cell: Cell): number {
    return this.getCellCenter(cell).x;
  }

  protected getCellCenterY(cell: Cell): number {
    return this.getCellCenter(cell).y;
  }
}
